// src/pages/po/AprovacaoItemsSidePanelContent.tsx
//
// Side panel para a fila de aprovacao (coluna "Aguardando aprovacao" do
// Kanban de Requisicoes). Lista itens em status=APROVAR da requisicao
// selecionada e oferece 2 modos exclusivos:
//
//   - Aprovar: usuario seleciona itens + qtdAprovada (default=qtdSolicitada),
//     dispara PUT /v1/requisicoes/{id}/itens/aprovar em batch. Resposta
//     vem item-a-item: aprovados removidos da lista (viraram PENDENTE/
//     FINALIZADO), parciais permanecem (statusItemApos=APROVAR).
//
//   - Reprovar: usuario seleciona itens + qtdReprovada + motivo livre por item
//     (ate 25 caracteres), dispara o mesmo PUT em batch com qtdAprovada=0,
//     qtdReprovada e motivo auditavel por item.
//
// Cuidados:
//   - ADR-005: nao reproduzir useEffect+reset() externo aqui. Parent passa
//     `key={requisicao.id}` para forcar remount e clear de state em mudanca.
//   - Visibilidade dos botoes Aprovar/Reprovar condicionada a
//     hasPermission('requisicoes.aprovar_requisicao_compra').
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClockRotateLeft } from '@/icons';
import axios from 'axios';
import ScrollableTable from '@/components/common/ScrollableTable';
import CloseButtonSidePanel from '@/components/common/CloseButtonSidePanel';
import RejectButton from '@/components/common/RejectButton';
import SaveButton from '@/components/common/SaveButton';
import GiveUpButton from '@/components/common/GiveUpButton';
import Modal from '@/components/common/Modal';
import HistoricoItemModalContent from './HistoricoItemModalContent';
import { RequisicaoItem, RequisicaoHeader } from '@/types/compras/Requisicao';
import { HistoricoResponse } from '@/types/compras/Historico';
import { AprovacaoItemResponse, AprovarItem } from '@/types/aprovacao/Aprovacao';
import { apiGet, apiPut } from '@/services/api';
import { useApiCall } from '@/hooks/useApiCall';
import { usePaginatedFetch } from '@/hooks/usePaginatedFetch';
import { useOperacaoLote } from '@/hooks/useOperacaoLote';
import { chaveLote, useOperacoesLote } from '@/contexts/OperacaoLoteContext';
import {
  contadoresInline,
  contarSelecionados,
  fragmentoSelectAll,
  mensagemResultado,
  textoSelecao,
  type ModoSelecao,
} from '@/utils/selectAll';
import { useAuth } from '@/contexts/AuthContext';
import { showAlert } from '@/contexts/DialogContext';
import { Toast } from '@/contexts/ToastContext';
import { formatDateTime, formatDateOrDash } from '@/utils/formatUtils';
import '@/pages/po/RequisicaoItemsSidePanelContent.css';

interface AprovacaoItemsSidePanelContentProps {
  // somaCustoTotalAprovar ja vinha aqui em runtime — o Requisicoes.tsx passa o
  // objeto Requisicao inteiro (:491) —, o tipo e que estava estreitado a { id }.
  // E o mesmo numero que o card do Kanban exibe como "Total a aprovar", vindo de
  // GET /v1/requisicoes/capa?itens=aprovar.
  requisicao: { id: number; somaCustoTotalAprovar?: number };
  theme: 'dark' | 'light';
  onClose: () => void;
  onDataChange?: () => void;
}

type Modo = 'view' | 'approve' | 'reject';

const formatBRL = (v: number | null | undefined): string => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const AprovacaoItemsSidePanelContent: React.FC<AprovacaoItemsSidePanelContentProps> = ({
  requisicao,
  theme,
  onClose,
  onDataChange,
}) => {
  const { hasPermission, role } = useAuth();
  const podeAprovar = hasPermission('requisicoes.aprovar_requisicao_compra');
  const isMaster = role === 'ROLE_MASTER';

  const [header, setHeader] = useState<RequisicaoHeader | null>(null);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [modo, setModo] = useState<Modo>('view');
  const [selectedItems, setSelectedItems] = useState<RequisicaoItem[]>([]);
  // (selectionResetKey saiu na limpeza: nesta tela a seleção é controlada pelo
  //  painel nos dois modos, então não há estado interno da tabela a resetar.)
  const [approveQuantities, setApproveQuantities] = useState<{ [key: number]: number }>({});
  const [rejectQuantities, setRejectQuantities] = useState<{ [key: number]: number }>({});
  const [rejectReasons, setRejectReasons] = useState<{ [key: number]: string }>({});
  const [historicoData, setHistoricoData] = useState<HistoricoResponse | null>(null);
  const [historicoModalOpen, setHistoricoModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const { callApi, isLoading: isSaving } = useApiCall();

  // --- "Selecionar tudo entre paginas" (issue #31) ---------------------------
  // 'enumerado' = comportamento historico: a tabela e dona da selecao e o payload
  // leva a lista de itens. 'todos' = modo complemento: o servidor resolve o
  // conjunto por filtro.itens='aprovar' e o front manda so o que foi DESmarcado.
  const [modoSelecao, setModoSelecao] = useState<ModoSelecao>('enumerado');
  const [excludeIds, setExcludeIds] = useState<Set<number>>(new Set());
  // No modo 'todos' o motivo nao pode ser por item: a maior parte do conjunto
  // nunca foi carregada na tela. O backend recebe um motivo unico do lote.
  const [motivoPadrao, setMotivoPadrao] = useState('');
  const { disparar, estaProcessando } = useOperacaoLote();
  const { aoConcluir } = useOperacoesLote();

  // Espelha a trava (acao, chave_pai) do backend. APROVAR cobre aprovar E
  // reprovar: sao o mesmo endpoint, e a decisao vive em decisaoPadrao.
  const chaveTrava = chaveLote('APROVAR', requisicao.id);
  const processandoLote = estaProcessando(chaveTrava);

  const fetchItems = useCallback(
    async (pageNumber: number): Promise<{ items: RequisicaoItem[]; totalItems: number }> => {
      const queryParams = new URLSearchParams({
        page: pageNumber.toString(),
        size: '10',
        itens: 'aprovar',
      });
      const res = await apiGet(`/v1/requisicoes/${requisicao.id}/itens?${queryParams.toString()}`);
      const {
        dataCriacao,
        nroContato,
        status,
        origem,
        codFilial,
        usuarioId,
        nomeFantasia,
        nomeUsuario,
        nomeCliente,
        codCliente,
        codConsultor,
        nomeConsultor,
        dataEncerramento,
        aprovaMaximoRequisicaoUsuario,
        categoriasPermitidasUsuario,
        produtosExcluidosUsuario,
        ativo,
        somaCustoAprovavelUsuario,
        qtdItensAprovaveisUsuario,
        itens,
      } = res.data;
      if (pageNumber === 0) {
        setHeader({
          dataCriacao,
          nroContato,
          status,
          origem,
          codFilial,
          usuarioId,
          nomeFantasia,
          nomeUsuario,
          nomeCliente,
          codCliente,
          codConsultor,
          nomeConsultor,
          dataEncerramento,
          aprovaMaximoRequisicaoUsuario,
          categoriasPermitidasUsuario,
          produtosExcluidosUsuario,
          ativo,
          somaCustoAprovavelUsuario,
          qtdItensAprovaveisUsuario,
        });
        setTotalItems(itens.page.totalElements);
      }
      const items: RequisicaoItem[] = itens.content || [];
      return { items, totalItems: itens.page.totalElements };
    },
    [requisicao.id]
  );

  const { items, hasMore, isLoading, loadMore, updateItems, reset } = usePaginatedFetch<RequisicaoItem>({
    fetchFunction: fetchItems,
    pageSize: 10,
    uniqueKey: 'id',
    initialPage: 0,
  });

  const handleHistoricoClick = async (item: RequisicaoItem) => {
    try {
      const queryParams = new URLSearchParams({
        codProduto: item.codProduto,
        codCategoriaProduto: item.codCategoriaProduto.toString(),
        itemModuloId: item.id.toString(),
        page: '0',
        size: '10',
      });
      const res = await apiGet(`/v1/historico-itens?${queryParams.toString()}`);
      setHistoricoData(res.data);
      setSelectedItemId(item.id);
      setHistoricoModalOpen(true);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        await showAlert(err.response.data.error, { variant: 'error' });
      } else {
        await showAlert('Erro ao carregar o histórico. Tente novamente.', { variant: 'error' });
      }
    }
  };

  const handleCloseHistoricoModal = () => {
    setHistoricoModalOpen(false);
    setHistoricoData(null);
    setSelectedItemId(null);
  };

  const clearModeState = () => {
    setSelectedItems([]);
    setApproveQuantities({});
    setRejectQuantities({});
    setRejectReasons({});
    setModoSelecao('enumerado');
    setExcludeIds(new Set());
    setMotivoPadrao('');
  };

  // Recarrega a lista quando um lote DESTE pai termina — mas só se o painel
  // ainda estiver aberto. Se o usuário já saiu, ninguém assina e não há o que
  // atualizar: a próxima abertura busca do servidor de qualquer forma.
  //
  // O handler vive num ref para a inscrição não ser refeita a cada render (as
  // funções abaixo são recriadas em todo ciclo). Assim o efeito depende só da
  // chave, que é estável enquanto a requisição for a mesma.
  const aoConcluirLoteRef = useRef<() => void>(() => {});
  aoConcluirLoteRef.current = () => {
    clearModeState();
    setModo('view');
    onDataChange?.();
    reset();
  };

  useEffect(
    () => aoConcluir(chaveTrava, () => aoConcluirLoteRef.current()),
    [aoConcluir, chaveTrava],
  );

  const handleEnterApprove = () => {
    clearModeState();
    setModo('approve');
  };

  const handleEnterReject = () => {
    clearModeState();
    setModo('reject');
  };

  const handleGiveUp = () => {
    clearModeState();
    setModo('view');
  };

  // `onSelectChange` é prop obrigatória do ScrollableTable, mas nesta tela ela
  // nunca dispara: a tabela só a chama pelo caminho de seleção INTERNO, e aqui a
  // seleção é controlada pelo painel sempre que há checkbox na tela. Fica como
  // no-op explícito para ninguém procurar o handler que não existe.
  const handleSelectChange = () => {};

  // --- Selecao: convencao de checkbox de cabecalho de tabela ------------------
  //
  // O cabecalho E o "selecionar tudo", e "tudo" e a FILA INTEIRA, nao a pagina
  // carregada. Nao ha segundo gesto nem botao a parte.
  //
  //   vazio   -> clicar marca a fila inteira (modo 'todos');
  //   traco   -> parte selecionada; clicar limpa;
  //   marcado -> tudo selecionado; clicar limpa.

  const limparSelecao = () => {
    setModoSelecao('enumerado');
    setExcludeIds(new Set());
    setSelectedItems([]);
  };

  /** Textos distintos e não vazios já digitados na coluna Motivo. */
  const motivosDistintosDigitados = (): string[] => {
    const vistos = new Set<string>();
    Object.values(rejectReasons).forEach((texto) => {
      const t = (texto ?? '').trim();
      if (t !== '') vistos.add(t);
    });
    return [...vistos];
  };

  const entrarEmTodos = () => {
    // O modo 'todos' não tem motivo por item, e não é escolha de tela: o backend
    // exige a quantidade junto do motivo no override (`AprovarItem.qtdAprovada`
    // é @NotNull sob @Valid), e calcular essa quantidade aqui seria copiar a
    // regra do saldo decisional para o navegador. Então a coluna Motivo sai e um
    // campo único assume.
    //
    // O que muda aqui é o SILÊNCIO. Antes, marcar a última linha descartava sem
    // aviso o que o usuário tinha escrito — e descartava do payload também, não
    // só da tela: o motivo por item só sobrevivia nas linhas cuja quantidade
    // também tivesse sido editada, que é justamente o caso RARO.
    if (modo === 'reject') {
      const distintos = motivosDistintosDigitados();
      if (distintos.length === 1) {
        // Um texto só não é ambíguo — vira o motivo do lote direto, sem
        // interromper o usuário para redigitar o que ele já escreveu.
        setMotivoPadrao(distintos[0]);
      } else if (distintos.length > 1) {
        // Textos diferentes não têm como virar um só sem escolher por ele.
        // Campo em branco + aviso: a decisão volta para quem sabe.
        setMotivoPadrao('');
        void showAlert('Para reprovar todos os itens um único motivo deve ser inserido.', {
          variant: 'warning',
        });
      }
    }
    setModoSelecao('todos');
    setExcludeIds(new Set());
    setSelectedItems([]);
  };

  const handleSelecionarItem = (item: RequisicaoItem, marcado: boolean) => {
    if (modoSelecao === 'todos') {
      // Desmarcar aqui ALIMENTA a lista de exclusao — e o que permite dizer
      // "todos menos este" sem o navegador precisar conhecer todos. O cabecalho
      // cai para 'traco', que e o estado real: parte selecionada.
      setExcludeIds((prev) => {
        const next = new Set(prev);
        if (marcado) next.delete(item.id);
        else next.add(item.id);
        return next;
      });
      return;
    }

    const proximos = marcado
      ? [...selectedItems, item]
      : selectedItems.filter((it) => it.id !== item.id);

    // Convencao: marcar na mao todas as linhas selecionaveis equivale a marcar o
    // cabecalho — e o cabecalho significa a fila inteira. A faixa deixa explicito
    // que a acao passou a valer para itens que nao estao na tela.
    const selecionaveis = items.filter((it) => !blockedIds.has(it.id));
    if (marcado && selecionaveis.length > 0 && proximos.length === selecionaveis.length) {
      entrarEmTodos();
      return;
    }
    setSelectedItems(proximos);
  };

  const normalizeQuantityInput = (rawValue: string): number => {
    const normalized = rawValue.replace(',', '.');
    if (!normalized || normalized === '.') return 0;
    const [intRaw, decRaw = ''] = normalized.split('.');
    const intPart = intRaw === '' ? '0' : intRaw;
    const decPart = decRaw.slice(0, 3);
    const parsed = Number.parseFloat(decPart ? `${intPart}.${decPart}` : intPart);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const setApproveQty = (itemId: number, raw: string, maxQty: number) => {
    let n = normalizeQuantityInput(raw);
    if (n > 0 && n < 0.001) n = 0.001;
    if (n > maxQty) n = maxQty;
    n = Number(n.toFixed(3));
    setApproveQuantities((prev) => ({ ...prev, [itemId]: n }));
  };

  const setRejectQty = (itemId: number, raw: string, maxQty: number) => {
    let n = normalizeQuantityInput(raw);
    if (n > 0 && n < 0.001) n = 0.001;
    if (n > maxQty) n = maxQty;
    n = Number(n.toFixed(3));
    setRejectQuantities((prev) => ({ ...prev, [itemId]: n }));
  };

  // Decide quais ids saem da lista apos um batch.
  //   (a) statusItemApos = PENDENTE | FINALIZADO  → item ja transitou no backend.
  //   (b) aprovado=true e o saldo a decidir zerou pos-deltas
  //       (qtdSaldoRequisicao - deltas do batch <= 0). Cobre o caso em que
  //       backend devolve statusItemApos='APROVAR' (ou null) mesmo apos
  //       decisao total — frontend detecta via aritmetica para que o item
  //       suma da fila e o auto-close do side panel funcione.
  // Tolerancia 0.0005 evita residuo de soma decimal (inputs usam step=0.001).
  const computeIdsToRemove = (
    responses: AprovacaoItemResponse[],
    currentItems: RequisicaoItem[]
  ): Set<number> => {
    const byId = new Map(currentItems.map((it) => [it.id, it]));
    const ids = new Set<number>();
    responses.forEach((r) => {
      if (r.statusItemApos === 'PENDENTE' || r.statusItemApos === 'FINALIZADO') {
        ids.add(r.requisicaoItemId);
        return;
      }
      if (!r.aprovado) return;
      const it = byId.get(r.requisicaoItemId);
      if (!it) return;
      const saldoApos = it.qtdSaldoRequisicao - r.qtdAprovada - (r.qtdReprovada ?? 0);
      if (saldoApos <= 0.0005) {
        ids.add(r.requisicaoItemId);
      }
    });
    return ids;
  };

  const applyResponseToList = (responses: AprovacaoItemResponse[]) => {
    // Items removidos: ver computeIdsToRemove. Items que permanecem com
    // statusItemApos=APROVAR e aprovado=true recebem deltas in-place.
    const idsToRemove = computeIdsToRemove(responses, items);
    const deltas = new Map<number, { aprovadaDelta: number; reprovadaDelta: number }>();
    responses.forEach((r) => {
      if (
        r.aprovado &&
        r.statusItemApos === 'APROVAR' &&
        !idsToRemove.has(r.requisicaoItemId)
      ) {
        deltas.set(r.requisicaoItemId, {
          aprovadaDelta: r.qtdAprovada,
          reprovadaDelta: r.qtdReprovada ?? 0,
        });
      }
    });

    if (idsToRemove.size === 0 && deltas.size === 0) return;

    updateItems((prev) =>
      prev
        .filter((it) => !idsToRemove.has(it.id))
        .map((it) => {
          const d = deltas.get(it.id);
          if (!d) return it;
          return {
            ...it,
            qtdAprovada: (it.qtdAprovada ?? 0) + d.aprovadaDelta,
            qtdReprovada: (it.qtdReprovada ?? 0) + d.reprovadaDelta,
            qtdSaldoRequisicao: it.qtdSaldoRequisicao - d.aprovadaDelta - d.reprovadaDelta,
          };
        })
    );
    if (idsToRemove.size > 0) {
      setTotalItems((t) => Math.max(0, t - idsToRemove.size));
    }
  };

  const summarizeBatchResponse = async (responses: AprovacaoItemResponse[]) => {
    const aprovados = responses.filter((r) => r.aprovado);
    const rejeitados = responses.filter((r) => !r.aprovado);
    if (aprovados.length > 0) {
      const totalAprovado = aprovados.reduce((sum, r) => sum + (r.valorAprovado ?? 0), 0);
      Toast.success(
        `${aprovados.length} item(ns) aprovado(s) — total ${formatBRL(totalAprovado)}.`
      );
    }
    if (rejeitados.length > 0) {
      const listaMotivos = rejeitados
        .map((r) => `• Item #${r.requisicaoItemId}: ${r.motivo ?? 'motivo nao informado'}`)
        .join('\n');
      await showAlert(
        `${rejeitados.length} item(ns) nao aprovado(s):\n${listaMotivos}`,
        { variant: 'warning', title: 'Aprovacao parcial' }
      );
    }
  };

  // Fim do caminho INLINE do modo complemento. O assíncrono não passa por aqui:
  // quem avisa é o OperacaoLoteProvider, porque o usuário pode já ter saído.
  //
  // A lista é RELIDA inteira em vez de receber deltas: o front não tem como
  // aplicar resposta item-a-item sobre itens que nunca carregou.
  //
  // O CORPO É LIDO, não descartado. O 200 do selectAll é o mesmo array de
  // decisões do modo enumerado (mesmo `processarAprovacao` no backend), e item
  // recusado por alçada volta nele com `aprovado:false` — sem checagem de "houve
  // ao menos um aprovado". Sem contar essas recusas, um lote inteiramente
  // recusado saía como toast verde de sucesso. Os contadores saem pela mesma
  // regra do 202 (`aprovado === true` é processado), então a segunda linha é
  // idêntica nos dois caminhos.
  const finalizarInline = (data: unknown) => {
    const decisoes = (data ?? []) as AprovacaoItemResponse[];
    const msg = mensagemResultado({ assincrono: false, ...contadoresInline(decisoes) });
    clearModeState();
    setModo('view');
    onDataChange?.();
    reset();
    if (msg.variante === 'success') {
      Toast.success(msg.texto);
      return;
    }
    // Houve item ignorado: modal com OK, e não toast. Mesmo tratamento que o
    // provider dá ao 202 — a informação é a mesma e não pode sumir sozinha.
    void showAlert(msg.detalhe ? `${msg.texto}\n${msg.detalhe}` : msg.texto, {
      variant: msg.variante,
      title: 'Processamento concluído',
    });
  };

  const dispararSelectAll = async (payload: Record<string, unknown>) => {
    try {
      const resultado = await disparar({
        chaveTrava,
        executar: () => apiPut(`/v1/requisicoes/${requisicao.id}/itens/aprovar`, payload),
        onInline: finalizarInline,
      });

      // Trava local: barra o segundo clique antes de gastar a request. O backend
      // tem a sua propria (400 enquanto o lote do mesmo pai esta EM_ANDAMENTO).
      if (resultado === 'bloqueado') {
        await showAlert('Já existe um processamento em andamento para esta requisição.', {
          variant: 'warning',
        });
        return;
      }

      // Aceito para segundo plano: o provider ja avisou o usuario e vai avisar de
      // novo no fim, onde quer que ele esteja. Aqui so devolvemos a tela ao
      // estado neutro — a acao saiu das maos dele. A lista NAO e relida agora:
      // ainda nao ha o que reler, e quem recarrega no fim e a inscricao no topo
      // do componente, se o painel ainda estiver aberto.
      if (resultado === 'aceito') {
        clearModeState();
        setModo('view');
        onDataChange?.();
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        await showAlert(err.response.data.error, { variant: 'error' });
      } else {
        await showAlert('Erro ao processar os itens. Tente novamente.', { variant: 'error' });
      }
    }
  };

  // Filtro da listagem que o servidor reproduz. So 'aprovar' traz itens
  // acionaveis por esta acao; qualquer outro valor o backend rejeita com 400.
  const filtroSelectAll = { itens: 'aprovar' };

  const handleSaveApprove = async () => {
    if (modoSelecao === 'todos') {
      // Zero significa a MESMA coisa nos dois modos de seleção. Antes, o zero era
      // filtrado para fora dos overrides em silêncio e o servidor aprovava o item
      // pela quantidade padrão — o oposto do gesto. Só as linhas editadas entram
      // na checagem; as demais não têm entrada aqui e recebem o default.
      const zerados = Object.entries(approveQuantities).filter(
        ([id, qtd]) => !excludeIds.has(Number(id)) && qtd <= 0,
      );
      if (zerados.length > 0) {
        await showAlert(
          'Quantidade aprovada deve ser maior que zero. Para reprovar use o botão "Reprovar".',
          { variant: 'warning' }
        );
        return;
      }
      // Overrides sao as linhas VISIVEIS que o usuario editou a mao. As demais
      // recebem o default por item do servidor. Ids fora do conjunto resolvido
      // sao ignorados em silencio pelo backend (aplicarOverrides so aplica o que
      // existe no conjunto materializado); filtramos os desmarcados mesmo assim.
      const overrides = Object.entries(approveQuantities)
        .map(([id, qtd]) => ({ requisicaoItemId: Number(id), qtdAprovada: qtd }))
        .filter((o) => !excludeIds.has(o.requisicaoItemId));
      await dispararSelectAll({
        ...fragmentoSelectAll<typeof filtroSelectAll, number>({
          filtro: filtroSelectAll,
          excludeIds: [...excludeIds],
        }),
        decisaoPadrao: 'APROVAR',
        ...(overrides.length > 0 ? { overrides } : {}),
      });
      return;
    }
    if (selectedItems.length === 0) {
      await showAlert('Selecione pelo menos um item para aprovar.', { variant: 'warning' });
      return;
    }
    // Validacao: qtdAprovada deve ser > 0 (se 0, eh reprovacao — usar modo reject).
    // Fallback usa calcRestanteAprovar para alinhar com o que o input
    // exibe — evita divergencia entre tela e payload quando user nao edita o campo.
    const itensPayload = selectedItems.map((it) => ({
      requisicaoItemId: it.id,
      qtdAprovada: approveQuantities[it.id] ?? calcRestanteAprovar(it),
    }));
    if (itensPayload.some((p) => p.qtdAprovada <= 0)) {
      await showAlert(
        'Quantidade aprovada deve ser maior que zero. Para reprovar use o botão "Reprovar".',
        { variant: 'warning' }
      );
      return;
    }
    try {
      await callApi(
        'put',
        `/v1/requisicoes/${requisicao.id}/itens/aprovar`,
        { itens: itensPayload },
        {
          onSuccess: async (data: unknown) => {
            const responses = (data ?? []) as AprovacaoItemResponse[];
            // Snapshot ANTES do applyResponseToList para decidir auto-close.
            const removedIds = computeIdsToRemove(responses, items);
            const willBeEmpty = items.filter((it) => !removedIds.has(it.id)).length === 0;

            applyResponseToList(responses);
            clearModeState();
            setModo('view');
            onDataChange?.();

            // Aguarda dismiss de todos os showAlert dentro do summarize.
            await summarizeBatchResponse(responses);

            // Sem itens restantes — fecha o side panel (handleCloseAprovacaoPanel
            // no parent ja chama reloadAllColumns, atualizando o card do Kanban).
            if (willBeEmpty) onClose();
          },
        }
      );
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        await showAlert(err.response.data.error, { variant: 'error' });
      } else {
        await showAlert('Erro ao aprovar itens. Tente novamente.', { variant: 'error' });
      }
    }
  };

  const handleSaveReject = async () => {
    if (modoSelecao === 'todos') {
      const motivo = motivoPadrao.trim();
      // O backend aceita motivoPadrao vazio, mas esta tela sempre exigiu motivo
      // na reprovacao. Manter a exigencia preserva a paridade (premissa #9) — o
      // que muda e o escopo: um motivo para o lote, nao um por item, porque a
      // maior parte do conjunto nunca foi carregada na tela.
      if (motivo === '') {
        await showAlert('Informe o motivo da reprovação.', { variant: 'warning' });
        return;
      }
      // Mesma paridade do aprovar: zero é recusado, não filtrado em silêncio.
      const zerados = Object.entries(rejectQuantities).filter(
        ([id, qtd]) => !excludeIds.has(Number(id)) && qtd <= 0,
      );
      if (zerados.length > 0) {
        await showAlert('Quantidade a reprovar deve ser maior que zero.', { variant: 'warning' });
        return;
      }
      // O motivo é UM só, e é o do lote — inclusive nas linhas que também tiveram
      // a quantidade editada. Herdar ali o texto por item contradiria o aviso que
      // `entrarEmTodos` acabou de dar ("um único motivo deve ser inserido") e
      // faria duas linhas idênticas na tela irem com motivos diferentes.
      const overrides = Object.entries(rejectQuantities)
        .map(([id, qtd]) => ({
          requisicaoItemId: Number(id),
          qtdAprovada: 0,
          qtdReprovada: qtd,
          motivo,
        }))
        .filter((o) => !excludeIds.has(o.requisicaoItemId));
      await dispararSelectAll({
        ...fragmentoSelectAll<typeof filtroSelectAll, number>({
          filtro: filtroSelectAll,
          excludeIds: [...excludeIds],
        }),
        decisaoPadrao: 'REPROVAR',
        motivoPadrao: motivo,
        ...(overrides.length > 0 ? { overrides } : {}),
      });
      return;
    }
    if (selectedItems.length === 0) {
      await showAlert('Selecione pelo menos um item para reprovar.', { variant: 'warning' });
      return;
    }
    // Validacao do motivo
    const missingReason = selectedItems.some(
      (it) => !rejectReasons[it.id] || rejectReasons[it.id].trim() === ''
    );
    if (missingReason) {
      await showAlert('Informe um motivo para todos os itens.', { variant: 'warning' });
      return;
    }
    // Reprovacao parcial suportada pelo backend. Payload inclui motivo
    // livre por item e flag explicita de qtdReprovada informada.
    // Fallback usa calcRestanteReprovar — alinha com o que o input exibe.
    const itensPayload = selectedItems.map((it) => ({
      requisicaoItemId: it.id,
      qtdAprovada: 0,
      qtdReprovada: rejectQuantities[it.id] ?? calcRestanteReprovar(it),
      motivo: rejectReasons[it.id].trim(),
      qtdReprovadaInformada: true,
    }) satisfies AprovarItem);
    if (itensPayload.some((p) => p.qtdReprovada <= 0)) {
      await showAlert('Quantidade a reprovar deve ser maior que zero.', { variant: 'warning' });
      return;
    }
    try {
      await callApi(
        'put',
        `/v1/requisicoes/${requisicao.id}/itens/aprovar`,
        { itens: itensPayload },
        {
          onSuccess: async (data: unknown) => {
            const responses = (data ?? []) as AprovacaoItemResponse[];
            // Snapshot ANTES do applyResponseToList para decidir auto-close.
            const removedIds = computeIdsToRemove(responses, items);
            const willBeEmpty = items.filter((it) => !removedIds.has(it.id)).length === 0;

            applyResponseToList(responses);
            clearModeState();
            setModo('view');
            onDataChange?.();

            const sucessos = responses.filter((r) => r.aprovado);
            const falhas = responses.filter((r) => !r.aprovado);
            if (sucessos.length > 0) {
              Toast.success(`${sucessos.length} item(ns) reprovado(s).`);
            }
            if (falhas.length > 0) {
              const motivos = falhas
                .map((r) => `• Item #${r.requisicaoItemId}: ${r.motivo ?? 'motivo nao informado'}`)
                .join('\n');
              // await: bloqueia auto-close ate user dismissar o alert.
              await showAlert(
                `Falhas:\n${motivos}`,
                { variant: 'warning', title: 'Reprovacao parcial' }
              );
            }

            if (willBeEmpty) onClose();
          },
        }
      );
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        await showAlert(err.response.data.error, { variant: 'error' });
      } else {
        await showAlert('Erro ao reprovar itens. Tente novamente.', { variant: 'error' });
      }
    }
  };

  // Exibe coluna Observação apenas quando ao menos um item tem texto.
  const hasObsItem = items.some(
    (it) => typeof it.obsItem === 'string' && it.obsItem.trim() !== ''
  );

  // Envolve o conteudo de uma celula para aplicar cinza visual quando o item
  // esta bloqueado por parametrizacao e o painel esta em modo de edicao.
  // Em modo view nao pintamos a linha (decisao D2 da Fase 2).
  const wrapCell = (it: RequisicaoItem, content: React.ReactNode): React.ReactNode => {
    if (modo === 'view' || !blockedIds.has(it.id)) return content;
    return <span className="row-blocked-cell">{content}</span>;
  };

  const observacaoColumns = hasObsItem
    ? [
        {
          header: 'Observação',
          render: (it: RequisicaoItem) => wrapCell(it, it.obsItem ?? ''),
        },
      ]
    : [];

  const baseColumns = [
    { header: 'Código', render: (it: RequisicaoItem) => wrapCell(it, it.codProduto) },
    {
      header: 'Cód. Categoria',
      render: (it: RequisicaoItem) => wrapCell(it, it.codCategoriaProduto),
    },
    { header: 'Nome do Produto', render: (it: RequisicaoItem) => wrapCell(it, it.nomeProduto) },
    { header: 'Categoria', render: (it: RequisicaoItem) => wrapCell(it, it.nomeCategoria) },
    { header: 'Qtd. Solicitada', render: (it: RequisicaoItem) => wrapCell(it, it.qtdSolicitada) },
    {
      header: 'Qtd. Aprovada',
      render: (it: RequisicaoItem) => wrapCell(it, it.qtdAprovada ?? 0),
    },
    {
      header: 'Qtd. Reprovada',
      render: (it: RequisicaoItem) => wrapCell(it, it.qtdReprovada ?? 0),
    },
    { header: 'Qtd. Devolvida', render: (it: RequisicaoItem) => wrapCell(it, it.qtdDevolvida) },
    {
      header: 'Qtd. Pendente',
      render: (it: RequisicaoItem) => wrapCell(it, calcRestanteAprovar(it)),
    },
    {
      header: 'Custo Unit. Compra',
      render: (it: RequisicaoItem) => wrapCell(it, formatBRL(it.custoUnitarioUltimaCompra)),
    },
    {
      header: 'Última Compra',
      render: (it: RequisicaoItem) => wrapCell(it, formatDateOrDash(it.dataUltimaCompra)),
    },
    ...observacaoColumns,
  ];

  // Saldo a decidir = qtdSolicitada - qtdAprovada - qtdReprovada - qtdDevolvida.
  // Formula independe de qtdSaldoRequisicao (que tem semantica diferente entre
  // backend e state local pos-applyResponseToList). Mesmo valor usado pelo
  // input Qtd. Aprovar/Reprovar e pela coluna Qtd. Pendente.
  const calcRestanteAprovar = (it: RequisicaoItem): number =>
    Math.max(
      0,
      Number(
        (
          it.qtdSolicitada -
          (it.qtdAprovada ?? 0) -
          (it.qtdReprovada ?? 0) -
          (it.qtdDevolvida ?? 0)
        ).toFixed(3)
      )
    );

  const calcRestanteReprovar = calcRestanteAprovar;

  // Bloqueio de UI por parametrizacao de alcada do usuario (nao-MASTER).
  // Ativo apenas com role != ROLE_MASTER && header.ativo === true. Backend
  // continua sendo a fonte autoritativa — UI so esconde controles.
  const restricaoAtiva = !isMaster && header?.ativo === true;
  const categoriasPermitidas = header?.categoriasPermitidasUsuario ?? null;
  const produtosExcluidos = header?.produtosExcluidosUsuario ?? null;
  const aprovaMaximo = header?.aprovaMaximoRequisicaoUsuario ?? null;

  // O recorte que ESTE usuario pode aprovar, resolvido pelo servidor sobre a fila
  // inteira (api-blueltSystem#76). Substitui as duas aproximacoes que o front
  // fazia sozinho e que se contradiziam na mesma barra: uma exata mas parcial
  // (so os itens carregados), outra completa mas inflada (incluia o que o usuario
  // nao pode aprovar).
  //
  // null aqui e o caso de MASTER e de nao-MASTER sem parametrizacao: nao ha
  // alcada a aplicar, o numero de hoje ja e o certo, e TODO o comportamento
  // abaixo cai de volta no calculo anterior.
  const somaAprovavelUsuario = header?.somaCustoAprovavelUsuario ?? null;
  const qtdAprovavelUsuario = header?.qtdItensAprovaveisUsuario ?? null;

  const isBlocked = useCallback(
    (it: RequisicaoItem): boolean => {
      if (!restricaoAtiva) return false;
      if (
        categoriasPermitidas !== null &&
        !categoriasPermitidas.includes(it.codCategoriaProduto)
      ) {
        return true;
      }
      if (
        produtosExcluidos !== null &&
        produtosExcluidos.includes(`${it.codProduto}+${it.codCategoriaProduto}`)
      ) {
        return true;
      }
      return false;
    },
    [restricaoAtiva, categoriasPermitidas, produtosExcluidos]
  );

  const blockedIds = useMemo(
    () => new Set(items.filter((it) => isBlocked(it)).map((it) => it.id)),
    [items, isBlocked]
  );

  const algumNaoBloqueado = items.some((it) => !blockedIds.has(it.id));

  // Estado visual do checkbox do cabecalho, na convencao de tres estados.
  //
  // 'marcado' exige duas coisas: nada desmarcado pelo usuario E nenhum item
  // bloqueado por alcada entre os carregados. Um item bloqueado e um item que
  // NAO vai ser processado — afirmar "tudo selecionado" com ele na tela seria
  // mentira, e o traco e exatamente o estado real ("parte").
  //
  // So enxergamos os bloqueados que ja foram carregados; os das paginas seguintes
  // aparecem conforme o usuario rola. O sentido do erro e seguro: comeca podendo
  // dizer 'marcado' e cai para 'parcial' ao descobrir o primeiro bloqueado —
  // nunca o contrario. A contagem exata da fila depende do backend (issue #76).
  const estadoCabecalho: 'vazio' | 'parcial' | 'marcado' =
    modoSelecao === 'todos'
      ? excludeIds.size === 0 && blockedIds.size === 0
        ? 'marcado'
        : 'parcial'
      : selectedItems.length === 0
        ? 'vazio'
        : 'parcial';

  const handleToggleCabecalho = () => {
    if (estadoCabecalho === 'vazio') entrarEmTodos();
    else limparSelecao();
  };

  // ATENCAO (ADR-039 item 6): este numero vem da LISTAGEM e e um teto, nao uma
  // promessa. O conjunto que o servidor processa e a listagem filtrada aos
  // acionaveis — itens fora da alcada do aprovador caem em soft-fail e nao
  // entram na conta. Por isso a mensagem de fim NUNCA repete este numero: a
  // verdade e a contagem que a resposta reporta.
  // No modo 'todos' descontam-se DUAS coisas do total da fila: o que o usuário
  // desmarcou e os bloqueados por alçada que já conhecemos. Sem o segundo, uma
  // requisição de 5 itens em que o usuário só pode aprovar 1 exibia "5 de 5".
  //
  // Com `qtdItensAprovaveisUsuario` o servidor já entrega esse recorte resolvido
  // sobre a fila INTEIRA, não só sobre as linhas carregadas. Aí o desconto de
  // `blockedIds` vira contagem DUPLA — os bloqueados já ficaram de fora da base —
  // e some. Sem o campo (MASTER, ou sem parametrização), tudo segue como antes.
  const totalParaContagem = qtdAprovavelUsuario ?? totalItems;
  const descontoTodos =
    qtdAprovavelUsuario !== null ? excludeIds.size : excludeIds.size + blockedIds.size;
  const qtdSelecionada = contarSelecionados(
    modoSelecao,
    totalParaContagem,
    modoSelecao === 'enumerado' ? selectedItems.length : descontoTodos,
  );

  const temSelecao = modoSelecao === 'todos' ? qtdSelecionada > 0 : selectedItems.length > 0;

  // Quantidade efetiva por item para calculos da faixa, conforme modo atual.
  // Em modo view usa restanteAprovar (so para "Total aproximado"). Itens
  // bloqueados sempre retornam 0 — nao entram no calculo.
  const qtdEfetiva = (it: RequisicaoItem): number => {
    if (blockedIds.has(it.id)) return 0;
    if (modo === 'approve') return approveQuantities[it.id] ?? calcRestanteAprovar(it);
    if (modo === 'reject') return rejectQuantities[it.id] ?? calcRestanteReprovar(it);
    return calcRestanteAprovar(it);
  };

  // Total aproximado: soma sobre TODOS os itens carregados nao bloqueados,
  // usando a qtdEfetiva (input do usuario com fallback no restante).
  const totalAproximado = useMemo(
    () =>
      items.reduce(
        (sum, it) => sum + qtdEfetiva(it) * (it.custoUnitarioUltimaCompra ?? 0),
        0
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, blockedIds, modo, approveQuantities, rejectQuantities]
  );

  // Selecionado: soma apenas dos itens marcados no checkbox, mesma qtdEfetiva.
  const totalSelecionado = useMemo(
    () =>
      selectedItems.reduce(
        (sum, it) => sum + qtdEfetiva(it) * (it.custoUnitarioUltimaCompra ?? 0),
        0
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedItems, blockedIds, modo, approveQuantities, rejectQuantities]
  );

  // Teto do valor selecionado no modo 'todos'. O front não consegue somar o
  // conjunto inteiro — a maior parte dos itens nunca foi carregada —, então parte
  // do total que o backend já calculou para a requisição (o mesmo número do card
  // do Kanban) e desconta o que o usuário desmarcou.
  //
  // Base: custoUnitarioUltimaCompra × qtdSolicitada, IGUAL à de
  // somaCustoTotalAprovar (Requisicao.ts:72-74). Misturar com a qtdEfetiva dos
  // inputs daria um número que não fecha com nenhum dos dois.
  //
  // Erra para CIMA, de propósito: itens bloqueados por alçada entram na soma (o
  // servidor os recusa depois, item a item) e itens com aprovação parcial entram
  // pela quantidade solicitada, não pelo saldo. Para conferência de limite de
  // alçada, teto alto demais no máximo pede uma conferida; teto baixo demais faz
  // o usuário achar que está dentro do limite quando não está.
  //
  // Com `somaCustoAprovavelUsuario` a base deixa de errar para cima: o servidor
  // já exclui o que este usuário não pode aprovar. Mas a base muda de UNIDADE
  // junto — ela soma pelo SALDO DECISIONAL, não pela quantidade pedida —, então o
  // desconto tem de mudar com ela. Descontar por `qtdSolicitada` de uma base em
  // saldo tiraria a mais e produziria um teto BAIXO demais, que é exatamente a
  // direção perigosa descrita acima. `calcRestanteAprovar` é o saldo decisional
  // que esta tela já calcula, então as duas pontas falam a mesma unidade.
  const tetoSelecionadoTodos = useMemo(() => {
    const total = somaAprovavelUsuario ?? requisicao.somaCustoTotalAprovar;
    if (typeof total !== 'number') return null;
    const porSaldo = somaAprovavelUsuario !== null;
    const baseDoItem = (it: RequisicaoItem) =>
      porSaldo ? calcRestanteAprovar(it) : it.qtdSolicitada;

    const descontado = items
      .filter((it) => excludeIds.has(it.id))
      .reduce((sum, it) => sum + baseDoItem(it) * (it.custoUnitarioUltimaCompra ?? 0), 0);

    // As linhas VISÍVEIS que o usuário editou também mudam o que vai ser feito:
    // elas viajam no payload como `overrides` (ver o ramo 'todos' do handleSave).
    // Sem descontar a diferença, editar "Qtd. Aprovar" de 4 para 1 enviava 1 e
    // continuava exibindo o valor de 4 — a tela prometendo uma coisa e o envio
    // fazendo outra, que é o que a ADR-031 existe para impedir. Achado no smoke
    // manual do dev (requisição 191): exibia 4.104,81 onde o envio valia 1.386,12.
    //
    // Só as linhas EDITADAS entram. Uma linha intocada precisa produzir diferença
    // ZERO, e no caminho de fallback (base em `qtdSolicitada`) usar o valor
    // efetivo mudaria o número de itens parcialmente aprovados — comportamento
    // que esta correção não toca.
    const editadas = modo === 'reject' ? rejectQuantities : approveQuantities;
    const ajusteEdicoes = items
      .filter((it) => !excludeIds.has(it.id) && editadas[it.id] !== undefined)
      .reduce(
        (sum, it) =>
          sum + (baseDoItem(it) - editadas[it.id]) * (it.custoUnitarioUltimaCompra ?? 0),
        0,
      );

    return Math.max(total - descontado - ajusteEdicoes, 0);
  }, [
    somaAprovavelUsuario,
    requisicao.somaCustoTotalAprovar,
    items,
    excludeIds,
    modo,
    approveQuantities,
    rejectQuantities,
  ]);

  const approveColumns =
    modo === 'approve'
      ? [
          {
            header: 'Qtd. Aprovar',
            render: (it: RequisicaoItem) => {
              if (blockedIds.has(it.id)) return null;
              const restante = calcRestanteAprovar(it);
              return (
                <input
                  type="number"
                  min={0.001}
                  max={restante}
                  step={0.001}
                  value={approveQuantities[it.id] ?? restante}
                  onChange={(e) => setApproveQty(it.id, e.target.value, restante)}
                  className="quantity-input"
                />
              );
            },
            width: '110px',
          },
        ]
      : [];

  const rejectColumns =
    modo === 'reject'
      ? [
          {
            header: 'Qtd. Reprovar',
            render: (it: RequisicaoItem) => {
              if (blockedIds.has(it.id)) return null;
              const restante = calcRestanteReprovar(it);
              return (
                <input
                  type="number"
                  min={0.001}
                  max={restante}
                  step={0.001}
                  value={rejectQuantities[it.id] ?? restante}
                  onChange={(e) => setRejectQty(it.id, e.target.value, restante)}
                  className="quantity-input"
                />
              );
            },
            width: '110px',
          },
        ]
      : [];

  // Coluna Motivo POR ITEM — some na reprovacao em lote: ali o motivo e unico e
  // vive junto dos botoes. Manter a coluna significaria oferecer um campo que so
  // alcanca as linhas carregadas, enquanto a acao vale para a fila inteira.
  const rejectMotivoColumn =
    modo === 'reject' && modoSelecao === 'enumerado'
      ? [
          {
            header: 'Motivo',
            render: (it: RequisicaoItem) => {
              if (blockedIds.has(it.id)) return null;
              return (
                <input
                  type="text"
                  value={rejectReasons[it.id] || ''}
                  onChange={(e) =>
                    setRejectReasons((prev) => ({
                      ...prev,
                      [it.id]: e.target.value.slice(0, 25),
                    }))
                  }
                  maxLength={25}
                  placeholder="Até 25 caracteres"
                  className="reason-select"
                />
              );
            },
            width: '180px',
          },
        ]
      : [];

  const columns = [...baseColumns, ...approveColumns, ...rejectColumns, ...rejectMotivoColumn];

  return (
    // `aprovacao-panel`: layout em coluna flexivel, exclusivo desta tela.
    // Motivo — esta e a unica tela cujo RODAPE cresce: ela acumula ate cinco
    // textos na mesma barra (limite de alcada, total aproximado, selecionado ate,
    // contagem da selecao e, na reprovacao em lote, o campo de motivo unico).
    // Quando a barra quebra para uma segunda linha, a altura fixa em vh do
    // `.table-container` empurrava os botoes Salvar/Desistir para fora da tela.
    // Com a coluna flexivel a tabela recebe o espaco que sobra e encolhe sozinha.
    // As demais telas nao recebem a classe e seguem com a altura padrao.
    <div className={`contact-items-modal-content aprovacao-panel ${theme}`}>
      <CloseButtonSidePanel
        onClick={onClose}
        className="close-button-absolute"
        disabled={isLoading || isSaving}
      />
      <div className="title-table">
        <div>
          <div className="facelet-h3">
            <h3>
              Contato {header?.nroContato} | Requisição {requisicao.id} | Criada em{' '}
              {header ? formatDateTime(header.dataCriacao) : 'Carregando...'} | Situação: {header?.status}
            </h3>
          </div>
          <div className="facelet-p">
            <p>
              Origem:{' '}
              {header?.origem === 'PLANEJAMENTO'
                ? 'Planejamento'
                : header?.origem === 'BALCAO'
                  ? 'Balcão'
                  : 'Erro ao consultar origem'}{' '}
              | Loja: {header?.nomeFantasia} | Requisitante:{' '}
              {header?.nomeUsuario || 'Erro de Integração'}
            </p>
            <p>
              Código do Cliente: {header?.codCliente} | Nome do Cliente:{' '}
              {header?.nomeCliente || 'Erro de Integração'} | Código do consultor:{' '}
              {header?.codConsultor} | Nome do consultor: {header?.nomeConsultor}
            </p>
          </div>
        </div>
      </div>
      {isLoading && items.length === 0 ? (
        <p>Carregando itens...</p>
      ) : (
        <>
          <ScrollableTable<RequisicaoItem>
            data={items}
            hasMore={hasMore}
            loadMore={loadMore}
            isLoading={isLoading}
            columns={columns}
            onSelectChange={handleSelectChange}
            renderCheckbox={(item: RequisicaoItem) => !blockedIds.has(item.id)}
            renderHistoryAction={(item: RequisicaoItem) => (
              <button
                className="action-button"
                onClick={() => handleHistoricoClick(item)}
                title="Ver Histórico do Item"
              >
                <FontAwesomeIcon icon={faClockRotateLeft} />
              </button>
            )}
            emptyMessage="Nenhum item aguardando aprovacao."
            showCheckboxes={modo !== 'view'}
            showTotalItems={true}
            totalItems={totalItems}
            // Nesta tela a selecao e controlada pelo painel nos DOIS modos: o
            // cabecalho precisa significar "a fila inteira", e a comparacao por
            // id (em vez de referencia) sobrevive a lista ser reconstruida.
            // As outras telas nao recebem a prop e seguem inalteradas.
            selecaoEntrePaginas={
              modo !== 'view'
                ? {
                    estadoCabecalho,
                    // Itens bloqueados por alcada nao chegam aqui: renderCheckbox
                    // ja nao desenha checkbox para eles. O servidor ainda os
                    // resolve e os recusa um a um — por isso a faixa fala "ate N".
                    estaSelecionado: (it: RequisicaoItem) =>
                      modoSelecao === 'todos'
                        ? !excludeIds.has(it.id)
                        : selectedItems.some((s) => s.id === it.id),
                    onToggleCabecalho: handleToggleCabecalho,
                    onSelecionarItem: handleSelecionarItem,
                  }
                : undefined
            }
          />
          <div className="form-actions form-actions-aprovacao">
            {/* Uma barra só: o separador "|" vem da regra `span + span::before`
                do CSS, que só age entre spans IRMÃOS. A frase da seleção estava
                numa segunda barra e por isso saía sem separador.
                A condição cobre os dois casos — com alçada restrita (as três
                primeiras frases) e sem (MASTER vê apenas a da seleção). */}
            {(restricaoAtiva || (modo !== 'view' && estadoCabecalho !== 'vazio')) && (
              <div className="aprovacao-info-bar">
                {restricaoAtiva && aprovaMaximo !== null && (
                  <span>
                    Você pode aprovar até <strong>{formatBRL(aprovaMaximo)}</strong> por requisição.
                  </span>
                )}
                {/* Com o número do servidor a frase deixa de ser aproximação: ele
                    cobre a fila inteira, não só as linhas já roladas. Sem ele
                    (MASTER, ou sem parametrização), volta a ser o cálculo local
                    sobre os carregados — e o rótulo acompanha, porque prometer
                    exatidão sobre uma soma parcial é o defeito, não o texto. */}
                {restricaoAtiva && (
                  <span>
                    {somaAprovavelUsuario !== null ? 'Total a aprovar ' : 'Total aproximado a aprovar '}
                    <strong>{formatBRL(somaAprovavelUsuario ?? totalAproximado)}</strong>
                  </span>
                )}
                {restricaoAtiva && modo !== 'view' && modoSelecao === 'enumerado' && (
                  <span>
                    Selecionado <strong>{formatBRL(totalSelecionado)}</strong>
                  </span>
                )}
                {/* No modo 'todos', somar só os itens carregados mostraria um valor
                    muito abaixo do real — e num indicador de limite de alçada isso
                    é o erro perigoso. Exibe o teto; sem o total do card, exibe "—"
                    em vez de um número que não dá para calcular. */}
                {restricaoAtiva && modo !== 'view' && modoSelecao === 'todos' && (
                  <span>
                    Selecionado até{' '}
                    <strong>
                      {tetoSelecionadoTodos !== null ? formatBRL(tetoSelecionadoTodos) : '—'}
                    </strong>
                  </span>
                )}
                {/* Escopo da seleção — último span da barra, então recebe o "|" das
                    frases anteriores quando elas existem, e fica sozinho sem
                    separador quando não existem (MASTER). Informativo: o gesto é o
                    checkbox do cabeçalho, e nada mais. */}
                {modo !== 'view' && estadoCabecalho !== 'vazio' && (
                  <span>{textoSelecao(qtdSelecionada, totalParaContagem)}</span>
                )}
                {/* Motivo único do lote. Vive na MESMA barra para receber o "|"
                    da regra `span + span::before`, e é um span só (rótulo +
                    campo) para o separador não cair entre eles. */}
                {modo === 'reject' && modoSelecao === 'todos' && (
                  <span>
                    Motivo da reprovação (aplicado a todos os itens):{' '}
                    <input
                      type="text"
                      value={motivoPadrao}
                      onChange={(e) => setMotivoPadrao(e.target.value.slice(0, 25))}
                      maxLength={25}
                      placeholder="Até 25 caracteres"
                      disabled={processandoLote}
                      className="motivo-lote-input"
                    />
                  </span>
                )}
              </div>
            )}
            <div className="form-actions-buttons">
              {podeAprovar && modo === 'view' && items.length > 0 && algumNaoBloqueado && (
                <>
                  <button
                    type="button"
                    className="save-button"
                    onClick={handleEnterApprove}
                    disabled={isLoading || isSaving}
                    title="Aprovar itens"
                  >
                    <FontAwesomeIcon icon={faCheck} /> Aprovar
                  </button>
                  <RejectButton
                    onClick={handleEnterReject}
                    disabled={isLoading || isSaving}
                  />
                </>
              )}
              {modo !== 'view' && (
                <>
                  <GiveUpButton
                    onClick={handleGiveUp}
                    disabled={isLoading || isSaving || processandoLote}
                  />
                  {temSelecao && (
                    <SaveButton
                      isLoading={isSaving || processandoLote}
                      onClick={modo === 'approve' ? handleSaveApprove : handleSaveReject}
                      disabled={isLoading || isSaving || processandoLote}
                    />
                  )}
                </>
              )}
            </div>
          </div>
          <Modal
            isOpen={historicoModalOpen}
            onClose={handleCloseHistoricoModal}
            theme={theme}
            zIndex={2000}
          >
            {historicoData && selectedItemId && (
              <HistoricoItemModalContent
                historico={historicoData}
                requisicaoItemId={selectedItemId}
                isOpen={historicoModalOpen}
                onClose={handleCloseHistoricoModal}
                theme={theme}
                showCancelButton={false}
              />
            )}
          </Modal>
        </>
      )}
    </div>
  );
};

export default AprovacaoItemsSidePanelContent;
